if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();

      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };

      this.mql = window.matchMedia('(min-width: 768px)');

      // Support both event names used by the theme/custom swatch code.
      // Match the complete slide ID because Shopify prefixes media IDs
      // with the section ID (for example: "template--123__main-456").
      this.handleVariantChange = (event) => {
        const variant = event.detail?.variant;
        const viewer = this.elements.viewer;

        if (!variant?.featured_media || !viewer) return;

        const mediaId = String(variant.featured_media.id);

        const matchingSlide = Array.from(
          viewer.querySelectorAll('[data-media-id]')
        ).find((slide) =>
          String(slide.dataset.mediaId).endsWith('-' + mediaId)
        );

        if (!matchingSlide) {
          console.warn(
            'MediaGallery: no gallery slide found for variant media ID:',
            mediaId
          );
          return;
        }

        this.setActiveMedia(matchingSlide.dataset.mediaId, false);
      };

      document.addEventListener('variant:change', this.handleVariantChange);
      document.addEventListener('variant:changed', this.handleVariantChange);

      if (!this.elements.viewer || !this.elements.thumbnails) return;

      this.elements.viewer.addEventListener(
        'slideChanged',
        debounce(this.onSlideChanged.bind(this), 500)
      );

      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        const button = mediaToSwitch.querySelector('button');

        if (button) {
          button.addEventListener(
            'click',
            this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false)
          );
        }
      });

      if (this.dataset.desktopLayout?.includes('thumbnail') && this.mql.matches) {
        this.removeListSemantic();
      }
    }

    onSlideChanged(event) {
      if (!event.detail.currentElement || !this.elements.thumbnails) return;

      const mediaId = event.detail.currentElement.dataset.mediaId;
      const thumbnail = this.elements.thumbnails.querySelector(
        `[data-target="${mediaId}"]`
      );

      this.setActiveThumbnail(thumbnail);
    }

    setActiveMedia(mediaId, prepend) {
      if (!this.elements.viewer) return;

      // Do not silently select the first slide if the requested slide is missing.
      const activeMedia = Array.from(
        this.elements.viewer.querySelectorAll('[data-media-id]')
      ).find((element) => element.dataset.mediaId === String(mediaId));

      if (!activeMedia) {
        console.warn('MediaGallery: gallery slide not found:', mediaId);
        return;
      }

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });

      activeMedia.classList.add('is-active');

      if (prepend && activeMedia.parentElement) {
        activeMedia.parentElement.prepend(activeMedia);

        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${mediaId}"]`
          );

          if (activeThumbnail?.parentElement) {
            activeThumbnail.parentElement.prepend(activeThumbnail);
          }
        }

        if (this.elements.viewer.slider) {
          this.elements.viewer.resetPages();
        }
      }

      this.preventStickyHeader();

      window.setTimeout(() => {
        if (activeMedia.parentElement && this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }

        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      });

      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;

      const activeThumbnail = this.elements.thumbnails.querySelector(
        `[data-target="${mediaId}"]`
      );

      if (activeThumbnail) {
        this.setActiveThumbnail(activeThumbnail);

        if (this.elements.liveRegion) {
          this.announceLiveRegion(
            activeMedia,
            activeThumbnail.dataset.mediaPosition
          );
        }
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => {
        element.removeAttribute('aria-current');
      });

      const button = thumbnail.querySelector('button');
      if (button) button.setAttribute('aria-current', 'true');

      if (
        this.elements.thumbnails.isSlideVisible &&
        this.elements.thumbnails.isSlideVisible(thumbnail, 10)
      ) {
        return;
      }

      if (this.elements.thumbnails.slider) {
        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image || !this.elements.liveRegion) return;

      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);

        if (window.accessibilityStrings?.imageAvailable) {
          this.elements.liveRegion.innerHTML =
            window.accessibilityStrings.imageAvailable.replace('[index]', position);
        }

        window.setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };

      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      if (typeof window.pauseAllMedia === 'function') {
        window.pauseAllMedia();
      }

      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia && typeof deferredMedia.loadContent === 'function') {
        deferredMedia.loadContent(false);
      }
    }

    preventStickyHeader() {
      this.stickyHeader =
        this.stickyHeader || document.querySelector('sticky-header');

      if (!this.stickyHeader) return;

      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer?.slider) return;

      this.elements.viewer.slider.setAttribute('role', 'presentation');

      if (this.elements.viewer.sliderItems) {
        this.elements.viewer.sliderItems.forEach((slide) => {
          slide.setAttribute('role', 'presentation');
        });
      }
    }
  });
}
